import React, { useEffect, useState } from 'react';
import { Form, FormItem, Input, ResetButton, Select, SubmitButton } from 'formik-antd';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router';
import { Col, Row } from 'antd';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';
import { IntlMessages } from '@/components';
import Spinner from '../../../../components/Spinner';
import PreviewDictionaryStructure from '../../components/PreviewDictionaryStructure';
import makeSelectFilterOption from '../../../../lib/makeSelectFilterOption';
import { useDictionaries, useDictionariesDispatch } from '../../providers/DictionariesProvider';



const validationSchema = Yup.object().shape({
  name: Yup.string()
    .min(4, 'Min 4 symbols required')
    .max(50, 'Max 50 symbols required')
    .required('Name field is required'),
  source: Yup.string(),
  structure_id: Yup.string().required('Structure field is required'),
});

const FormCreateDictionary = ({
  onSubmit,
  isSubmitting,
  formErrors,
}) => {
  const intl = useIntl();
  const [ searchQueryParams ] = useSearchParams();
  const { sources, structures } = useDictionaries();
  const [ initialValues, setInitialValues ] = useState({});
  const dispatch = useDictionariesDispatch();

  const navigationSource = searchQueryParams.get('source') ?? null;
  const multipleSources = searchQueryParams.get('multipleSources') ?? null;
  const defaultSource = sources?.data?.find((item) => item.source === navigationSource);

  const filteredSources = multipleSources ?
    {
      'data': sources.data,
      'options': sources.options.filter((source) => multipleSources.split(',').map((item) => intl.formatMessage({ id: `structures-source-${item.toLowerCase()}` })).includes(source.label)),
    } : sources;

  useEffect(() => {
    if (defaultSource && !multipleSources) {
      setInitialValues((original) => {
        return { ...original, source: defaultSource.id };
      });

      dispatch({ type: 'SET_STRUCTURE', structureId: null });
      dispatch({ type: 'SET_SOURCE', source: defaultSource.id });
    }
  }, [ defaultSource ]);

  return (
    <Formik
      enableReinitialize
      validationSchema={validationSchema}
      isSubmitting={isSubmitting}
      initialValues={initialValues}
      initialErrors={formErrors}
      onSubmit={(values) => {
        onSubmit({ name: values.name.trim(), structure_id: values.structure_id });
      }}
    >
      {({ isValid, setFieldValue, dirty, resetForm }) => {
        return (
          <Form layout="vertical">
            <Spinner spinning={isSubmitting}>
              <Row gutter={[ 32, 32 ]}>
                <Col md={12} xs={24}>
                  <FormItem
                    className='ant-form-item-col'
                    name="name"
                    label={<IntlMessages id="ui-general-name" />}
                    required
                  >
                    <Input
                      name='name'
                      placeholder={intl.formatMessage({ id: 'ui-general-name' })}
                    />
                  </FormItem>

                  <FormItem
                    className='ant-form-item-col'
                    name="source"
                    label={<IntlMessages id="ui-dictionaries-source-field" />}
                    required
                  >
                    <Select
                      showSearch={{
                        filterOption: (input, option) => makeSelectFilterOption(option.label, input)
                      }}
                      name="source"
                      disabled={defaultSource && !multipleSources}
                      loading={sources.isLoading}
                      options={filteredSources.options}
                      placeholder={<IntlMessages id="form-dictionaries-source-placeholder" />}
                      onChange={(value) => {
                        dispatch({ type: 'SET_STRUCTURE', structureId: null });
                        setFieldValue('structure_id', null);
                        dispatch({ type: 'SET_SOURCE', source: value });
                      }}
                    />
                  </FormItem>

                  <FormItem
                    className='ant-form-item-col'
                    name="structure_id"
                    label={<IntlMessages id="form-dictionaries-structure-field" />}
                    required
                  >
                    <Select
                      name="structure_id"
                      loading={structures.isLoading}
                      options={structures.options}
                      placeholder={<IntlMessages id="form-dictionaries-structure-placeholder" />}
                      onChange={(value) => {
                        dispatch({ type: 'SET_STRUCTURE', structureId: value });
                        setFieldValue('structure_id', value);
                      }}
                    />
                  </FormItem>
                </Col>
                <Col md={12} xs={24}>
                  <PreviewDictionaryStructure />
                </Col>
              </Row>

              <Row gutter={[ 16, 16 ]} className='hp-mt-16' justify='end'>
                <Col>
                  <ResetButton
                    disabled={false}
                    onClick={() => resetForm(initialValues)}
                  >
                    <IntlMessages id='ui-general-reset' />
                  </ResetButton>
                </Col>

                <Col>
                  <SubmitButton
                    type="primary"
                    loading={isSubmitting}
                    disabled={!isValid || !dirty}
                  >
                    <IntlMessages id="ui-general-submit" />
                  </SubmitButton>
                </Col>
              </Row>
            </Spinner>
          </Form>
        );
      }}
    </Formik>
  );
};

FormCreateDictionary.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  formErrors: PropTypes.object,
  isSubmitting: PropTypes.bool.isRequired,
};

FormCreateDictionary.defaultProps = {
  formErrors: {},
};

export default FormCreateDictionary;
